import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { ArticleCard } from "./components/ArticleCard";
import { ArrowLeft, Users, ShieldCheck } from "lucide-react";

const articles = [
  {
    title: "1099 Subcontractor vs. W-2 Employee Guide for Cleaning Companies",
    excerpt:
      "A legal and operational guide to hiring 1099 cleaning subcontractors vs. W-2 employees, tracking COI expiration dates, and avoiding IRS misclassification audits.",
    readTime: 8,
    href: "/success-hub/article/janitorial-subcontractor-vs-employee-guide",
  },
  {
    title: "Commercial Cleaning Subcontractor Agreement: Essential Clauses & SOW",
    excerpt:
      "Key legal clauses for janitorial subcontractor agreements: scope of work, pay terms, general liability minimums, and non-solicitation protections.",
    readTime: 7,
    href: "/success-hub/article/janitorial-subcontractor-agreement-template",
  },
  {
    title: "Certificate of Insurance (COI) Tracking & Compliance for Janitorial Vendors",
    excerpt:
      "How commercial cleaning companies manage COI expiration alerts, additional insured endorsements, and workers' compensation waivers for 1099 crews.",
    readTime: 6,
    href: "/success-hub/article/coi-insurance-tracking-commercial-cleaning",
  },
  {
    title: "Janitorial Staff Training & Safety SOP: Zero-Defect Onboarding",
    excerpt:
      "Structured 4-day onboarding covering OSHA HazCom chemical safety, chemical dwell times, and color-coded microfiber cross-contamination.",
    readTime: 7,
    href: "/success-hub/article/janitorial-staff-training-safety-sop",
  },
  {
    title: "Commercial Cleaning Crew Scheduling, Route Density & Dispatch SOP",
    excerpt:
      "Standard operating procedure for scheduling commercial cleaning crews, optimizing multi-building route density, and eliminating unbillable travel time.",
    readTime: 7,
    href: "/success-hub/article/commercial-cleaning-crew-scheduling-sop",
  },
  {
    title: "Non-Solicitation & Preventing Subcontractor Client Poaching",
    excerpt:
      "Enforceable non-solicitation agreements and operational controls that prevent 1099 subcontractors from taking your commercial cleaning accounts.",
    readTime: 6,
    href: "/success-hub/article/preventing-subcontractor-poaching-non-solicitation",
  },
  {
    title: "Calculating Janitorial Labor Burden: FICA, Workers' Comp & Overtime Math",
    excerpt:
      "How to accurately calculate true burdened labor costs (10%–15% on top of base wages) to protect profit margins on commercial cleaning contracts.",
    readTime: 8,
    href: "/success-hub/article/janitorial-labor-burden-payroll-taxes",
  },
  {
    title: "Cleaner Performance KPIs, Attendance Tracking & Bonus Structures",
    excerpt:
      "Implement objective performance metrics, mobile check-in verification, and monthly attendance bonuses to reduce technician turnover.",
    readTime: 6,
    href: "/success-hub/article/commercial-cleaning-technician-performance-kpis",
  },
];

export default function StaffCompliancePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO
        title="Staffing & Subcontractor Compliance | Commercial Cleaning Playbooks"
        description="Legal and operational frameworks for commercial cleaning companies: 1099 subcontractor agreements, COI tracking, burdened labor calculations, and crew SOPs."
        keywords={[
          "cleaning subcontractor compliance",
          "1099 vs W2 janitorial",
          "cleaning certificate of insurance tracking",
          "janitorial labor burden",
          "commercial cleaning crew training",
        ]}
        url="/success-hub/staff-compliance"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Success Hub", url: "/success-hub" },
            { name: "Staffing & Subcontractor Compliance", url: "/success-hub/staff-compliance" },
          ]),
        ]}
      />
      <LandingNavbar />

      {/* Header */}
      <section className="pb-12 pt-28">
        <div className="container mx-auto px-4">
          <Link
            to="/success-hub"
            className="mb-6 inline-flex items-center text-sm text-slate-600 transition-colors hover:text-blue-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Success Hub
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-start gap-6"
          >
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-purple-100">
              <Users className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <h1 className="mb-3 text-3xl font-bold text-slate-900 md:text-4xl">
                Staffing & Subcontractor Compliance
              </h1>
              <p className="max-w-2xl text-lg text-slate-600">
                Legal and operational frameworks for 1099 cleaning subcontractors, COI insurance compliance, burdened labor formulas, and crew management.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles List */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-4"
            >
              {articles.map((article, index) => (
                <ArticleCard
                  key={index}
                  title={article.title}
                  excerpt={article.excerpt}
                  category="Staff & Compliance"
                  categoryColor="bg-purple-100 text-purple-700"
                  readTime={article.readTime}
                  href={article.href}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
